import React from 'react'

import { Flex, Stack } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { observer } from 'mobx-react-lite'

import { ChangeTrackingPanel } from '#components/ChangeTracking/Panel'
import { DevToolbar, EditorToolbar } from '#components/EditorToolbar'
import { ScriptMiniHeader } from '#components/MiniHeader'
import { EditorRevisionsModal } from '#components/Modals/RevisionsModal'
import { PrompterView } from '#components/PrompterView'
import { ScriptStatusBar } from '#components/ScriptStatusBar'
import { HyperlinkPopover } from '#components/SelectionPopovers'
import { ScriptToastHost } from '#components/Toast'
import { useDebugFlag, useFeatureFlag } from '#hooks'
import { InlineInkStyles } from '#ink/components'
import { EditorHost } from '#layouts/Script/EditorHost'
import { ILoadedScript, useMst } from '#state'

import { CommentsPanel } from './CommentsPanel'
import { InlineStyles } from './InlineStyles'
import { ScriptWrapper } from './ScriptWrapper'
import { SnapshotsPanel } from './SnapshotsPanel'

export const Script = observer(function Script({
  script,
}: {
  script: ILoadedScript
}) {
  const { currentInkProject, currentOrg, socketManager, user, view } = useMst()

  const [showVisibleSplitPoints] = useDebugFlag('split-points')
  const [legacyPaginator] = useFeatureFlag('legacy-paginator')
  const pageless = user.prefs.pageless || script.isInk
  const noWrap = script.isInk && !user.inkPreferences?.lineWrap
  const liveEditorView = script.pmEditor.editorManager?.view

  const [pmHost, pmHostRef] = React.useState<HTMLDivElement | null>(null)
  const [
    snapshotsPanelIsOpen,
    { toggle: toggleSnapshotsPanel, close: closeSnapshotsPanel },
  ] = useDisclosure(false)
  const [
    changeTrackingPanelIsOpen,
    { toggle: toggleChangeTrackingPanel, close: closeChangeTrackingPanel },
  ] = useDisclosure(false)
  const [revisionsOpened, revisionsModal] = useDisclosure(false)

  const isDisconnected =
    !socketManager.connected || script.syncStatus.isTooStaleForSafety

  React.useLayoutEffect(() => {
    if (pmHost) {
      if (!liveEditorView || liveEditorView.dom.parentNode !== pmHost) {
        script.updateSelfFromEditor()
        script.launchEditor(pmHost)
      }
    } else if (!pmHost && liveEditorView) {
      script.tearDownEditor()
    }
  }, [pmHost, liveEditorView, script])

  // When switching view modes or view sizes, trigger prosemirror re-render
  // so things like avatar and comment positioning get updated
  React.useEffect(() => {
    script.pmEditor.rerender()
  }, [
    view.dimensions.scriptScroller.height,
    view.dimensions.scriptScroller.width,
    view.editorZoom,
    pageless,
    noWrap,
    script.pmEditor,
    showVisibleSplitPoints,
  ])

  // The question of "what plugins should be enabled?" is answered by the plugin
  // factory's `checkEnabled` method. This is not a reactive property though: to
  // trigger a re-creation of the plugin array (while keeping the editor state),
  // add the observed value to this effect's dependency array
  React.useEffect(() => {
    // Bail if the editor is still initializing
    if (!liveEditorView) return

    script.pmEditor.editorManager?.reloadPlugins(script)
  }, [script, liveEditorView, legacyPaginator])

  // `isEditable` only reaches the editor on start or room join, so push
  // plan restriction changes to the live view when they flip
  const isRestricted = !!script.readOnlyByPlan
  const prevIsRestricted = React.useRef(isRestricted)
  React.useEffect(() => {
    if (!liveEditorView) return
    if (prevIsRestricted.current === isRestricted) return
    prevIsRestricted.current = isRestricted

    const { editorManager } = script.pmEditor
    if (isRestricted) {
      editorManager?.setEditable(false, script)
    } else if (!isDisconnected) {
      editorManager?.setEditable(script.isEditable, script)
    }
  }, [script, liveEditorView, isRestricted, isDisconnected])

  return (
    <Stack gap={0}>
      {script.isInk && currentInkProject && (
        <InlineInkStyles
          messages={currentInkProject.compilerMessagesForScript(script.id)}
        />
      )}
      <InlineStyles script={script} />
      <ScriptMiniHeader />
      {currentOrg && (
        <EditorToolbar
          script={script}
          org={currentOrg}
          toggleSnapshotsPanel={toggleSnapshotsPanel}
          toggleChangeTrackingPanel={toggleChangeTrackingPanel}
          openRevisionsModal={revisionsModal.open}
        />
      )}
      <DevToolbar script={script} />
      <Flex h="100%" styles={{ root: { overflow: 'hidden' } }}>
        <ScriptWrapper
          pageCount={script.pageCount ?? 1}
          pageless={pageless}
          noWrap={noWrap}
          zoomLevel={view.editorZoom}
          blockFormats={script.blockFormats}
          paginationType={script.paginationType}
          pageLayout={script.pageLayout}
          showMarginRuler={user.prefs.showMarginRuler}
          fontCode={script.fontCode}
          hasLockedPages={script.hasLockedPages}
        >
          <EditorHost
            script={script}
            isDisconnected={isDisconnected}
            pmHostRef={pmHostRef}
          />
          {script.showPrompterView && <PrompterView />}
          {!script.isInk && <HyperlinkPopover />}
        </ScriptWrapper>
        <CommentsPanel script={script} />
        <SnapshotsPanel
          scriptId={script.id}
          isOpen={snapshotsPanelIsOpen}
          onClose={closeSnapshotsPanel}
        />
        <ChangeTrackingPanel
          script={script}
          isOpen={changeTrackingPanelIsOpen}
          onClose={closeChangeTrackingPanel}
        />
      </Flex>
      <ScriptStatusBar openRevisionsModal={revisionsModal.open} />
      <EditorRevisionsModal
        opened={revisionsOpened}
        onClose={revisionsModal.close}
      />
      <ScriptToastHost />
    </Stack>
  )
})
